import type { RuteroConfirmedOptionalItem, RuteroPublicSelectedOptions } from '@bandido/shared/types';

export interface ConfirmedOptionalItemRow extends RuteroConfirmedOptionalItem {
  /** El operador confirmó más unidades de las que el asesor pidió. */
  providerAdded: boolean;
  /** Solo un cero explícito autoriza a decir "sin costo". */
  free: boolean;
}

/**
 * Cruza lo CONFIRMADO por el operador con los códigos enviados en el hold.
 * La identidad es el código opaco del contrato, nunca el nombre visible. Una
 * cantidad confirmada mayor a la pedida se presenta como agregada por el
 * operador. Código ausente significa que el hub no pudo correlacionarlo: la
 * procedencia queda desconocida, no se inventa. Un precio ausente tampoco se
 * interpreta como gratis.
 */
export function confirmedOptionalItemRows({
  confirmed,
  selected,
}: {
  confirmed: readonly RuteroConfirmedOptionalItem[] | null | undefined;
  selected: Pick<RuteroPublicSelectedOptions, 'optionalItemCodes'> | undefined;
}): ConfirmedOptionalItemRow[] {
  if (!confirmed?.length) return [];
  const hasSelectionEvidence = selected !== undefined;
  const requested = new Map<string, number>();
  for (const code of selected?.optionalItemCodes ?? []) {
    requested.set(code, (requested.get(code) ?? 0) + 1);
  }
  return confirmed.map((item) => ({
    ...item,
    providerAdded: Boolean(
      hasSelectionEvidence &&
      item.detailBookingCode &&
      (requested.get(item.detailBookingCode) ?? 0) < item.quantity,
    ),
    free: item.price?.amountMinor === 0,
  }));
}

/**
 * La confirmación original siempre conserva su propio bloque cuando contiene
 * adicionales. `/extras` describe las compras cancelables ACTUALES, pero no
 * trae un vínculo contractual con cada unidad de este snapshot histórico; no
 * se fusionan dos fuentes con identidades distintas.
 */
export function confirmedOptionalItemsFallbackVisible({
  confirmed,
}: {
  confirmed: readonly RuteroConfirmedOptionalItem[] | null | undefined;
}): boolean {
  return Boolean(confirmed?.length);
}
